import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { perspectiveAPI } from '../services/api';
import { logger } from '../utils/logger';

interface PerspectiveState {
  targetUserId: string;
  targetUserName: string;
  targetUserEmail: string;
  currentPerspective: string;
  switchedAt: string;
  expiresAt: string;
}

interface PerspectiveDisplayInfo {
  targetUserName: string;
  targetUserEmail: string;
  perspectiveType: string;
  switchedAt: string;
  expiresAt: string;
}

interface SwitchResult {
  success: boolean;
  perspectiveContext?: PerspectiveState;
  error?: string;
}

interface PerspectiveContextValue {
  // State
  perspectiveContext: PerspectiveState | null;
  isInSwitchedView: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  switchToClientView: (targetUserId: string, reason?: string) => Promise<SwitchResult>;
  switchToRepView: (targetUserId: string, reason?: string) => Promise<SwitchResult>;
  returnToAdminView: () => Promise<SwitchResult>;
  clearPerspective: () => void;
  refreshPerspective: (trustLocal?: boolean) => Promise<void>;

  // Helpers
  getEffectiveUserId: () => string | null;
  getPerspectiveDisplayInfo: () => PerspectiveDisplayInfo | null;
}

const PerspectiveContext = createContext<PerspectiveContextValue | undefined>(undefined);

export const usePerspective = (): PerspectiveContextValue => {
  const context = useContext(PerspectiveContext);
  if (!context) {
    throw new Error('usePerspective must be used within a PerspectiveProvider');
  }
  return context;
};

/**
 * Helper to get initial perspective state from sessionStorage synchronously
 */
const getInitialPerspectiveState = (): { perspectiveContext: PerspectiveState | null; isInSwitchedView: boolean } => {
  try {
    const storedPerspective = sessionStorage.getItem('barebiologics_perspective');
    if (storedPerspective) {
      const parsed = JSON.parse(storedPerspective);
      return {
        perspectiveContext: parsed,
        isInSwitchedView: true,
      };
    }
  } catch (e) {
    sessionStorage.removeItem('barebiologics_perspective');
  }
  return {
    perspectiveContext: null,
    isInSwitchedView: false,
  };
};

interface PerspectiveProviderProps {
  children: ReactNode;
}

export const PerspectiveProvider: React.FC<PerspectiveProviderProps> = ({ children }) => {
  // Initialize state synchronously from sessionStorage to avoid flash
  const initialState = getInitialPerspectiveState();
  const [perspectiveContext, setPerspectiveContext] = useState<PerspectiveState | null>(initialState.perspectiveContext);
  const [isInSwitchedView, setIsInSwitchedView] = useState(initialState.isInSwitchedView);
  const [isLoading, setIsLoading] = useState(false); // Start as false since we hydrated from sessionStorage
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentPerspective = useCallback(async (trustLocal = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await perspectiveAPI.getCurrentPerspective();

      if (response.success) {
        // If server confirms an active session, update state
        if (response.data.isInSwitchedView && response.data.perspectiveContext) {
          setPerspectiveContext(response.data.perspectiveContext);
          setIsInSwitchedView(true);
          sessionStorage.setItem('barebiologics_perspective', JSON.stringify(response.data.perspectiveContext));
        } else if (!trustLocal) {
          // Only clear local state if we're not trusting local state
          setPerspectiveContext(null);
          setIsInSwitchedView(false);
          sessionStorage.removeItem('barebiologics_perspective');
        } else {
          logger.log('PerspectiveContext: Server returned no session but trusting local state');
        }
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch perspective';
      logger.error('Failed to fetch current perspective:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in (has auth token)
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return;
    }

    // Check stored user type - only internal users can have perspective sessions
    const storedUser = sessionStorage.getItem('barebiologics_currentUser');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.userType?.toUpperCase() !== 'INTERNAL') {
          return;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    const storedPerspective = sessionStorage.getItem('barebiologics_perspective');
    const hasLocalPerspective = !!storedPerspective;

    fetchCurrentPerspective(hasLocalPerspective);
  }, [fetchCurrentPerspective]);

  const switchToClientView = useCallback(async (targetUserId: string, reason?: string): Promise<SwitchResult> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await perspectiveAPI.switchToClientView(targetUserId, reason);

      if (response.success && response.data.perspectiveContext) {
        const newContext = response.data.perspectiveContext;
        setPerspectiveContext(newContext);
        setIsInSwitchedView(true);
        sessionStorage.setItem('barebiologics_perspective', JSON.stringify(newContext));
        return { success: true, perspectiveContext: newContext };
      }

      return { success: false, error: response.message || 'Failed to switch view' };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch view';
      logger.error('Failed to switch to client view:', err);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const switchToRepView = useCallback(async (targetUserId: string, reason?: string): Promise<SwitchResult> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await perspectiveAPI.switchToRepView(targetUserId, reason);

      if (response.success && response.data.perspectiveContext) {
        const newContext = response.data.perspectiveContext;
        setPerspectiveContext(newContext);
        setIsInSwitchedView(true);
        sessionStorage.setItem('barebiologics_perspective', JSON.stringify(newContext));
        return { success: true, perspectiveContext: newContext };
      }

      return { success: false, error: response.message || 'Failed to switch view' };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch view';
      logger.error('Failed to switch to rep view:', err);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const returnToAdminView = useCallback(async (): Promise<SwitchResult> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await perspectiveAPI.returnToAdminView();

      if (response.success) {
        setPerspectiveContext(null);
        setIsInSwitchedView(false);
        sessionStorage.removeItem('barebiologics_perspective');
        return { success: true };
      }

      return { success: false, error: response.message || 'Failed to return to admin view' };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to return to admin view';
      logger.error('Failed to return to admin view:', err);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearPerspective = useCallback(() => {
    setPerspectiveContext(null);
    setIsInSwitchedView(false);
    setError(null);
    sessionStorage.removeItem('barebiologics_perspective');
  }, []);

  const getEffectiveUserId = useCallback((): string | null => {
    if (isInSwitchedView && perspectiveContext?.targetUserId) {
      return perspectiveContext.targetUserId;
    }
    return null;
  }, [isInSwitchedView, perspectiveContext]);

  const getPerspectiveDisplayInfo = useCallback((): PerspectiveDisplayInfo | null => {
    if (!isInSwitchedView || !perspectiveContext) {
      return null;
    }

    return {
      targetUserName: perspectiveContext.targetUserName,
      targetUserEmail: perspectiveContext.targetUserEmail,
      perspectiveType: perspectiveContext.currentPerspective,
      switchedAt: perspectiveContext.switchedAt,
      expiresAt: perspectiveContext.expiresAt,
    };
  }, [isInSwitchedView, perspectiveContext]);

  const value: PerspectiveContextValue = {
    // State
    perspectiveContext,
    isInSwitchedView,
    isLoading,
    error,

    // Actions
    switchToClientView,
    switchToRepView,
    returnToAdminView,
    clearPerspective,
    refreshPerspective: fetchCurrentPerspective,

    // Helpers
    getEffectiveUserId,
    getPerspectiveDisplayInfo,
  };

  return (
    <PerspectiveContext.Provider value={value}>
      {children}
    </PerspectiveContext.Provider>
  );
};

export default PerspectiveContext;
